// src/components/shared/EquipmentMap.tsx
import React, { useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { MapPin, LayoutGrid, ExternalLink } from 'lucide-react';

// Corrige ícones quebrados do Leaflet com bundlers (Webpack/Vite)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const PLACEHOLDER = '/placeholder-outdoor.png';

// Paleta de status (alinhada à Vitrine / Relatório)
const COLOR = {
  inativo:  '#6c757d',
  esgotado: '#f72585',
  quase:    '#f8961e',
  livre:    '#4cc9f0',
} as const;

export interface MapEquipment {
  equipmentId:       string;
  name:              string;
  category:          string;
  location:          string;
  latitude:          number | null;
  longitude:         number | null;
  isActive?:         boolean;
  occupancyRate?:    number;
  availableSlots?:   number;
  slots?:            number;
  nextAvailableDate?: string | null;
  photoUrl?:         string | null;
}

interface Props {
  equipments: MapEquipment[];
  height?:    number;
}

// Recalcula o tamanho do mapa ao entrar/sair do fullscreen ou redimensionar
function ResizeHandler() {
  const map = useMap();
  useEffect(() => {
    const handleResize = () => setTimeout(() => map.invalidateSize(), 200);
    document.addEventListener('fullscreenchange', handleResize);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('fullscreenchange', handleResize);
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);
  return null;
}

// Enquadra todos os pins automaticamente
function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 14);
    } else {
      map.fitBounds(points, { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
}

function useFlyTo() {
  const map = useMap();
  return (lat: number, lng: number) => {
    map.flyTo([lat, lng], 16, { duration: 1.2 });
  };
}

// Pin colorido por disponibilidade
const buildIcon = (color: string) =>
  L.divIcon({
    className: '',
    html: `
      <div style="
        width:28px;height:28px;border-radius:50% 50% 50% 0;
        background:${color};transform:rotate(-45deg);
        border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);
        display:flex;align-items:center;justify-content:center;">
        <div style="width:9px;height:9px;background:#fff;border-radius:50%;transform:rotate(45deg);"></div>
      </div>`,
    iconSize:    [28, 28],
    iconAnchor:  [14, 28],
    popupAnchor: [0, -26],
  });

const pinColor = (e: MapEquipment) => {
  if (e.isActive === false) return COLOR.inativo;
  if (e.availableSlots === 0) return COLOR.esgotado;
  if ((e.occupancyRate ?? 0) >= 70) return COLOR.quase;
  return COLOR.livre;
};

const formatDate = (d: string) => new Date(d).toLocaleDateString('pt-BR');

// Texto + cor da disponibilidade (mesma lógica da AvailabilityTag do relatório)
const availabilityInfo = (e: MapEquipment) => {
  if (e.isActive === false) {
    return { label: 'Inativo', color: COLOR.inativo };
  }
  if (e.availableSlots === 0) {
    return {
      label: e.nextAvailableDate ? `Libera ${formatDate(e.nextAvailableDate)}` : 'Esgotado',
      color: COLOR.esgotado,
    };
  }
  if (e.availableSlots != null) {
    const color = e.availableSlots <= 2 ? COLOR.quase : COLOR.livre;
    return {
      label: `${e.availableSlots} livre${e.availableSlots > 1 ? 's' : ''}`,
      color,
    };
  }
  return null;
};

function Markers({ valid }: { valid: MapEquipment[] }) {
  const flyTo = useFlyTo();

  return (
    <MarkerClusterGroup
      chunkedLoading
      maxClusterRadius={45}
      spiderfyOnMaxZoom
      showCoverageOnHover={false}
    >
      {valid.map(e => {
        const avail = availabilityInfo(e);

        return (
          <Marker
            key={e.equipmentId}
            position={[e.latitude as number, e.longitude as number]}
            icon={buildIcon(pinColor(e))}
          >
            <Popup>
              <div style={{ width: 210 }}>
                <img
                  src={e.photoUrl || PLACEHOLDER}
                  alt={e.name}
                  style={{
                    width: '100%',
                    height: 110,
                    objectFit: 'cover',
                    borderRadius: 8,
                    marginBottom: 6,
                  }}
                />

                <strong
                  onClick={() => flyTo(e.latitude as number, e.longitude as number)}
                  style={{ cursor: 'pointer', color: '#0f172a' }}
                  title="Clique para aproximar"
                >
                  {e.name}
                </strong>
                <br />

                <small style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#64748b' }}>
                  <MapPin size={12} /> {e.location}
                </small>

                {/* categoria + total de slots */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>{e.category}</span>

                  {e.slots != null && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#475569' }}>
                      <LayoutGrid size={12} />
                      {e.slots} {e.slots === 1 ? 'slot' : 'slots'}
                    </span>
                  )}
                </div>

                {/* tag de disponibilidade (igual ao relatório) */}
                {avail && (
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      backgroundColor: `${avail.color}15`,
                      color: avail.color,
                      border: `1px solid ${avail.color}30`,
                      marginBottom: 8,
                    }}
                  >
                    {avail.label}
                  </span>
                )}

                <div>
                  <a
                    href={`https://www.google.com/maps?q=${e.latitude},${e.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 13, fontWeight: 600, color: '#2563eb', textDecoration: 'none',
                    }}
                  >
                    <ExternalLink size={13} /> Abrir no Google Maps
                  </a>
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MarkerClusterGroup>
  );
}

export const EquipmentMap: React.FC<Props> = ({ equipments, height = 520 }) => {
  const valid = useMemo(
    () => equipments.filter(
      e => e.latitude != null && e.longitude != null &&
           !Number.isNaN(e.latitude) && !Number.isNaN(e.longitude)
    ),
    [equipments]
  );

  const points = useMemo(
    () => valid.map(e => [e.latitude as number, e.longitude as number] as [number, number]),
    [valid]
  );

  const fallbackCenter: [number, number] = [-4.166, -40.945];

  if (valid.length === 0) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center text-muted bg-light rounded"
        style={{ height: '100%', minHeight: height }}
      >
        <MapPin size={42} />
        <p className="mt-2 mb-0">Nenhum equipamento com coordenadas cadastradas.</p>
        <small>Adicione latitude e longitude no cadastro do equipamento.</small>
      </div>
    );
  }

  return (
    <MapContainer
      center={points[0] ?? fallbackCenter}
      zoom={13}
      style={{ height: '100%', width: '100%', minHeight: height, borderRadius: 12, zIndex: 0 }}
      scrollWheelZoom
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <ResizeHandler />
      <FitBounds points={points} />
      <Markers valid={valid} />
    </MapContainer>
  );
};
